import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Alert,
  Box,
  Typography,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAddUserMutation } from '@/features/useraccount/api';

const schema = z.object({
  username: z
    .string()
    .min(1)
    .refine((s) => !/[ !@#$%^&*()+\-=\[\]{};':"\\|,.<>\/?]/.test(s), {
      message: 'hasSpecialCharacters',
    }),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  profile: z.string().min(1),
});
type FormValues = z.infer<typeof schema>;

const PROFILE_OPTIONS = ['ADMIN', 'TREASURY', 'SUPPORT', 'OPERATOR'];

export interface AddUserModalProps {
  onClose: (created: boolean | null) => void;
}

export function AddUserModal({ onClose }: AddUserModalProps) {
  const { t } = useTranslation();
  const addUser = useAddUserMutation();
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null);
  const [usernameExists, setUsernameExists] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', first_name: '', last_name: '', profile: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    setUsernameExists(false);
    const res = await addUser.mutateAsync({
      username: values.username,
      firstname: values.first_name,
      lastname: values.last_name,
      profile: values.profile,
    });
    if (res.code === 0) {
      const result = res.result as { username: string; password: string };
      setCreated(result);
      form.reset();
    } else if (res.code === 63) {
      setUsernameExists(true);
    } else {
      setError(res.message);
    }
  });

  return (
    <Dialog
      open
      onClose={() => onClose(created ? true : null)}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!!created}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('pages.user-list.add-user', { defaultValue: 'Add user' })}
        {!created && (
          <IconButton onClick={() => onClose(null)} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {created ? (
          <Stack spacing={2}>
            <Alert severity="success">
              {t('pages.user-list.user-created', { defaultValue: 'User created successfully.' })}
            </Alert>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('pages.user-list.table.header.username', { defaultValue: 'Username' })}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {created.username}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('pages.profile.password', { defaultValue: 'Password' })}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                {created.password}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {usernameExists && (
                <Alert severity="warning">
                  {t('pages.user-list.username-exists', { defaultValue: 'Username already exists.' })}
                </Alert>
              )}
              <Controller
                name="username"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('pages.user-list.table.header.username', { defaultValue: 'Username' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    size="small"
                  />
                )}
              />
              <Controller
                name="first_name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('pages.profile.first_name', { defaultValue: 'First name' })}
                    error={!!fieldState.error}
                    fullWidth
                    size="small"
                  />
                )}
              />
              <Controller
                name="last_name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('pages.profile.last_name', { defaultValue: 'Last name' })}
                    error={!!fieldState.error}
                    fullWidth
                    size="small"
                  />
                )}
              />
              <Controller
                name="profile"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    label={t('pages.user-list.table.header.job', { defaultValue: 'Profile' })}
                    error={!!fieldState.error}
                    fullWidth
                    size="small"
                  >
                    {PROFILE_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <Button variant="contained" onClick={() => onClose(true)}>
            {t('global.actions.close', { defaultValue: 'Close' })}
          </Button>
        ) : (
          <>
            <Button onClick={() => onClose(null)}>
              {t('global.actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={addUser.isPending || !form.formState.isValid}
            >
              {t('global.actions.create', { defaultValue: 'Create' })}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
